import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraOverlayDialog } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { proposalHref } from '../../lib/proposalHref';
import { useConvertCaptureToProposal } from '../../pages/Capture/hooks';
export function ConvertToProposalDialog({ open, onOpenChange, captureProjectId }) {
    const navigate = useNavigate();
    const convert = useConvertCaptureToProposal();
    // One key per opening action: retries and double submits reuse it so they
    // cannot create a second bid. A deliberate new bid rotates it.
    const openKey = useRef(crypto.randomUUID());
    const [opened, setOpened] = useState(null);
    useEffect(()=>{
        if (open) {
            openKey.current = crypto.randomUUID();
            setOpened(null);
        }
    }, [
        open
    ]);
    const run = async (deliberateNewBid)=>{
        if (deliberateNewBid) openKey.current = crypto.randomUUID();
        try {
            const summary = await convert.mutateAsync({
                captureProjectId,
                openRequestKey: openKey.current,
                deliberateNewBid
            });
            setOpened(summary);
            toast.success(deliberateNewBid ? 'Another bid started.' : 'Proposal workspace opened.');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to open Proposal');
        }
    };
    const goToProposal = ()=>{
        if (!opened) return;
        onOpenChange(false);
        navigate(proposalHref(opened.proposalProjectId));
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: "Open Proposal",
        description: /*#__PURE__*/ _jsx(_Fragment, {
            children: "Open the linked Proposal workspace, creating it if needed. Repeated opening returns the same Proposal. This Pursuit stays editable with its own lifecycle; its RFIs, interactions and workstreams remain available."
        }),
        testId: Testids.capture.convertDialog,
        className: "sm:max-w-[500px]",
        // Header + footer only until a Proposal is opened: collapse the shell's
        // body slot so no blank padded strip renders between them.
        scrollBody: false,
        focusableBody: false,
        bodyProps: {
            className: opened ? 'px-6 pb-4' : 'hidden'
        },
        footerProps: {
            cancelLabel: opened ? 'Close' : 'Cancel',
            onCancel: ()=>onOpenChange(false),
            cancelProps: {
                disabled: convert.isPending
            },
            leading: opened ? /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "secondary",
                disabled: convert.isPending,
                onClick: ()=>run(true),
                "data-testid": Testids.capture.convertAnotherBid,
                children: "Start another bid"
            }) : undefined,
            confirmLabel: opened ? 'Go to Proposal' : convert.isPending ? 'Opening…' : 'Open Proposal',
            onConfirm: opened ? goToProposal : ()=>run(false),
            confirmProps: {
                disabled: convert.isPending,
                'data-testid': opened ? Testids.capture.convertGoToProposal : Testids.capture.convertConfirm
            }
        },
        children: opened ? /*#__PURE__*/ _jsxs("dl", {
            className: "text-sm",
            "data-testid": Testids.capture.convertResult,
            children: [
                /*#__PURE__*/ _jsx("dt", {
                    className: "text-muted-foreground",
                    children: "Proposal"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    "data-testid": Testids.capture.convertProposalId,
                    children: opened.proposalProjectId
                }),
                /*#__PURE__*/ _jsx("dt", {
                    className: "mt-2 text-muted-foreground",
                    children: "Source"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    "data-testid": Testids.capture.convertSource,
                    children: opened.sourceKind
                }),
                /*#__PURE__*/ _jsx("dt", {
                    className: "mt-2 text-muted-foreground",
                    children: "Access"
                }),
                /*#__PURE__*/ _jsx("dd", {
                    "data-testid": Testids.capture.convertAccess,
                    children: opened.accessMode
                })
            ]
        }) : null
    });
}
